import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { ContactDialogProvider } from "@/components/ContactTrigger";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load secondary pages to reduce initial bundle size
const NotFound = lazy(() => import("./pages/NotFound"));
const Imprint = lazy(() => import("./pages/Imprint"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LegalInformation = lazy(() => import("./pages/LegalInformation"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const ForStartups = lazy(() => import("./pages/ForStartups"));
const CookieBanner = lazy(() => import("./components/CookieBanner"));

const queryClient = new QueryClient();

// Minimal page loading fallback
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CookieConsentProvider>
        <ContactDialogProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/imprint" element={
                <Suspense fallback={<PageFallback />}>
                  <Imprint />
                </Suspense>
              } />
              <Route path="/privacy-policy" element={
                <Suspense fallback={<PageFallback />}>
                  <PrivacyPolicy />
                </Suspense>
              } />
              <Route path="/legal-information" element={
                <Suspense fallback={<PageFallback />}>
                  <LegalInformation />
                </Suspense>
              } />
              <Route path="/cookie-policy" element={
                <Suspense fallback={<PageFallback />}>
                  <CookiePolicy />
                </Suspense>
              } />
              <Route path="/for-startups" element={
                <Suspense fallback={<PageFallback />}>
                  <ForStartups />
                </Suspense>
              } />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={
                <Suspense fallback={<PageFallback />}>
                  <NotFound />
                </Suspense>
              } />
            </Routes>
            <Suspense fallback={null}>
              <CookieBanner />
            </Suspense>
            <ScrollToTop />
          </BrowserRouter>
        </ContactDialogProvider>
      </CookieConsentProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
